import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BOOK_MENU_REACH } from './sidebarMap';
import './MobileDrawer.css';

const FRAMEWORK_BUILDER_PATH = BOOK_MENU_REACH.find(
  (item) => item.id === 'framework-builder',
)?.path;

function controlName(node) {
  return (node.getAttribute('aria-label') || node.textContent || '')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function MobileDrawer({ isOpen, onClose, children }) {
  const drawerRef = useRef(null);
  const previousFocusRef = useRef(null);
  const navigate = useNavigate();

  const openFrameworkBuilder = (event) => {
    const control = event.target.closest?.('button, a');
    if (!control || !drawerRef.current?.contains(control)) return;
    if (controlName(control) !== 'Framework Builder') return;
    if (!FRAMEWORK_BUILDER_PATH) return;
    event.preventDefault();
    event.stopPropagation();
    // /frameworks without this state is the chooser (browse). The builder
    // is the create view, the same door as Build with help.
    navigate(FRAMEWORK_BUILDER_PATH, { state: { openBuilder: Date.now() } });
    onClose?.();
  };

  // Save active element on open, restore on close
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap + auto-focus on open
  useEffect(() => {
    if (!isOpen) return;
    const drawer = drawerRef.current;
    if (!drawer) return;

    const focusableEls = drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    drawer.addEventListener('keydown', handleTab);
    firstEl?.focus();
    return () => drawer.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  return (
    <>
      <div
        className={`drawer-backdrop ${isOpen ? 'drawer-backdrop--open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <nav
        ref={drawerRef}
        className={`drawer-panel ${isOpen ? 'drawer-panel--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation drawer"
        onClickCapture={openFrameworkBuilder}
      >
        {children}
      </nav>
    </>
  );
}
